import React, { Component } from 'react';
import { observer, inject } from 'mobx-react';
import * as DataList from 'components/DataList';
import Pagination from 'components/Pagination';
import { materialStatus } from 'helpers/materialStatus';

// A purchase order's or material's status as a pill (helpers/materialStatus).
export const StatusPill = ({ status }) => {
  if (!status) return <DataList.Muted>–</DataList.Muted>;
  const { label, tone } = materialStatus(status);
  return <DataList.Pill tone={tone}>{label}</DataList.Pill>;
};

const purchaseOrderHref = (id) => `/dashboard/purchase-orders/${id}/show-purchase-order`;

const Pages = ({ meta, onChange }) =>
  meta && meta.total_pages > 1 ? (
    <div className="sup-pages">
      <Pagination value={meta.page} total={meta.total_pages} onChange={onChange} />
    </div>
  ) : null;

// The supplier's own search: all of its records, newest first. Not the shared
// Material orders search, whose filters (statuses, a work order) would leak
// into this list, and which this supplier would leak back into.
const supplierSearch = (supplier, page = 1) => ({
  where: { supplier_id: supplier.attributes.id },
  order: { created_at: 'desc' },
  per_page: 25,
  page,
});

// Materials bought from the supplier, with the purchase order and work order
// each is on.
export const MATERIAL_COLUMNS = [
  { area: 'item', label: 'Material', width: 'minmax(220px, 2fr)', bone: 'lines' },
  { area: 'quantity', label: 'Qty', width: 'minmax(56px, 0.4fr)', bone: 'short' },
  { area: 'po', label: 'Purchase order', width: 'minmax(130px, 1fr)', bone: 'short', icon: 'shopping-cart' },
  { area: 'wo', label: 'Work order', width: 'minmax(120px, 1fr)', bone: 'short', icon: 'clipboard' },
  { area: 'status', label: 'Status', width: 'minmax(150px, 1fr)', bone: 'pill' },
];
export const MATERIAL_CARDS = { columns: 'minmax(0, 1fr) auto', areas: ['item status', 'po wo', 'quantity quantity'] };

@inject('workReportStore') @observer
export class SupplierMaterials extends Component {
  page = 1;

  componentDidMount() {
    this.load();
  }

  load() {
    this.props.workReportStore.loadMaterialLineItems(supplierSearch(this.props.supplier, this.page));
  }

  changePage = (page) => {
    this.page = page;
    this.load();
  };

  render() {
    const { workReportStore } = this.props;
    const { isLoading, isError, meta } = workReportStore.materialLineItem;
    const objects = workReportStore.materialLineItem.objects || [];
    if (isLoading) return <DataList.Loading columns={MATERIAL_COLUMNS} cards={MATERIAL_CARDS} noun="materials" rows={5} />;
    if (isError) return <DataList.LoadError noFilters noun="materials" onRetry={() => this.load()} />;
    if (!objects.length) {
      return <DataList.Empty noun="materials" title="No materials yet" message="Materials ordered from this supplier on work orders show up here." />;
    }
    return (
      <React.Fragment>
        <DataList.List columns={MATERIAL_COLUMNS} cards={MATERIAL_CARDS} total={(meta && meta.total_records) || objects.length} noun="materials">
          {objects.map((material) => {
            const a = material.attributes;
            const po = a.purchase_order || {};
            const wo = a.work_report || {};
            return (
              <DataList.Row key={material.id}>
                <DataList.Cell area="item">
                  <DataList.Lines primary={a.description || 'Material'} secondary={a.part_number ? `Part ${a.part_number}` : undefined} />
                </DataList.Cell>
                <DataList.Cell area="quantity"><span className="sp-number">{a.quantity}</span></DataList.Cell>
                <DataList.Cell area="po">
                  {po.number ? <DataList.Lines primary={po.number} href={purchaseOrderHref(po.id)} /> : <DataList.Muted>None</DataList.Muted>}
                </DataList.Cell>
                <DataList.Cell area="wo">
                  {wo.number ? <DataList.Lines primary={wo.number} href={`/dashboard/work-reports/${wo.id}/show`} /> : <DataList.Muted>None</DataList.Muted>}
                </DataList.Cell>
                <DataList.Cell area="status"><StatusPill status={a.status} /></DataList.Cell>
              </DataList.Row>
            );
          })}
        </DataList.List>
        <Pages meta={meta} onChange={this.changePage} />
      </React.Fragment>
    );
  }
}

// Purchase orders sent to the supplier.
export const PO_COLUMNS = [
  { area: 'number', label: 'Purchase order', width: 'minmax(180px, 1.4fr)', bone: 'single' },
  { area: 'materials', label: 'Materials', width: 'minmax(100px, 0.8fr)', bone: 'short', icon: 'box' },
  { area: 'status', label: 'Status', width: 'minmax(150px, 1fr)', bone: 'pill' },
];
export const PO_CARDS = { columns: 'minmax(0, 1fr) auto', areas: ['number status', 'materials materials'] };

@inject('workReportStore') @observer
export class SupplierPurchaseOrders extends Component {
  page = 1;

  componentDidMount() {
    this.load();
  }

  load() {
    this.props.workReportStore.loadPurchaseOrders(supplierSearch(this.props.supplier, this.page));
  }

  changePage = (page) => {
    this.page = page;
    this.load();
  };

  render() {
    const { workReportStore } = this.props;
    const { isLoading, isError, meta } = workReportStore.purchaseOrderItem;
    const objects = workReportStore.purchaseOrderItem.objects || [];
    if (isLoading) return <DataList.Loading columns={PO_COLUMNS} cards={PO_CARDS} noun="purchase orders" rows={5} />;
    if (isError) return <DataList.LoadError noFilters noun="purchase orders" onRetry={() => this.load()} />;
    if (!objects.length) {
      return <DataList.Empty noun="purchase orders" title="No purchase orders yet" message="Purchase orders for this supplier show up here once they're made from a work order." />;
    }
    return (
      <React.Fragment>
        <DataList.List columns={PO_COLUMNS} cards={PO_CARDS} total={(meta && meta.total_records) || objects.length} noun="purchase orders">
          {objects.map((order) => {
            const a = order.attributes;
            const count = (a.material_line_items || []).length;
            return (
              <DataList.Row key={order.id}>
                <DataList.Cell area="number">
                  <DataList.RowLink href={purchaseOrderHref(a.id)} label={`Purchase order ${a.purchase_order_number || ''}`} />
                  <DataList.Lines primary={a.purchase_order_number || 'Purchase order'} />
                </DataList.Cell>
                <DataList.Cell area="materials">{count} {count === 1 ? 'material' : 'materials'}</DataList.Cell>
                <DataList.Cell area="status"><StatusPill status={a.status} /></DataList.Cell>
              </DataList.Row>
            );
          })}
        </DataList.List>
        <Pages meta={meta} onChange={this.changePage} />
      </React.Fragment>
    );
  }
}
